import React, { useEffect, useState } from 'react';
import { addDays, format } from 'date-fns';
import { useRouter } from 'next/router';
import 'react-datepicker/dist/react-datepicker.css';
import slugify from 'slugify';
import { HOME_PAGE, LOGIN_PAGE, SIGNUP_PAGE, TOURNAMENT_QUICK_CREATE_PAGE } from 'constants/pages';
import {
  BallTypesEnum,
  CompetitionGenderEnum,
  EventGroupFormatsEnum,
  EventPrivacyEnum,
  EventStatusesEnum,
  EventTypesEnum,
  ScoringFormatEnum,
  SportsEnum,
  TeamTypesEnum,
  useInsertDraftEventMutation,
  useInsertEventGroupMutation,
} from 'types/generated/client';
import { getTimeForMinutes } from 'utils/shared/coachBuilder';
import { buildDisplayLocation } from 'utils/shared/location/buildDisplayLocation';
import { buildTournamentEventNameWithoutGender } from 'utils/shared/string/buildTournamentEventName';
import { generateShortId } from 'utils/shared/string/generateShortId';
import { eventTypeEnumToString } from 'utils/shared/string/tournamentBuilder';
import { convertToUtc } from 'utils/shared/time/convertToUtc';
import { getTimezone } from 'utils/shared/time/getTimezone';
import { getTimezoneAbbreviation } from 'utils/shared/time/getTimezoneAbbreviation';
import { getTimezoneOffsetMinutes } from 'utils/shared/time/getTimezoneOffsetMinutes';
import {
  getMaximumVairCourts,
  getMinimumVairGames,
  getVairConfigForPlayerCount,
} from 'utils/shared/vair/scheduler';
import { getIsVairPro } from 'utils/shared/vair/vairSportStatus';
import { useModal } from 'hooks/useModal';
import { useVairPlayerSearch } from 'hooks/useVairPlayerSearch';
import { useViewer } from 'hooks/useViewer';
import ChevronLeft from 'svg/ChevronLeft';
import CloseIcon from 'svg/CloseIcon';
import LoadSpinner from 'svg/LoadSpinner';
import { ButtonLink } from 'components/Button';
import Modal from 'components/modals/Modal';
import Head from 'components/utilities/Head';
import CircularProgressBar from './components/CircularProgressBar';
import Events from './components/Events';
import ParticipantsAndGames from './components/ParticipantsAndGames';
import SelectVairFormat from './components/SelectVairFormat';
import Success from './components/Success';
import WhenAndWhere from './components/WhenAndWhere';
import { DEFAULT_VAIR_SCORE_LIMIT, FormData, VairFlow } from './types';

enum QuickCreateSteps {
  // The "Is this a VAIR event?" step (SelectVairEvent) was removed — normal users
  // land straight on WhenAndWhere as a non-VAIR event, while VAIR entries route to
  // SelectVairFormat via the ?vairFlow= param. Explicit values keep WhenAndWhere
  // 1-based so the CircularProgressBar numbering (getCurrentStep) stays correct.
  WhenAndWhere = 1,
  Events = 2,
  ParticipantsAndGames = 3,
  Success = 4,
  SelectVairFormat = 5,
}

export default function QuickCreatePage() {
  const { userId } = useViewer();

  const router = useRouter();
  const communityGroupId = router?.query?.groupId || null;
  const userTimeZone = getTimezone();
  const [currentStep, setCurrentStep] = useState<QuickCreateSteps>(
    QuickCreateSteps.WhenAndWhere,
  );
  const [vairFlow, setVairFlow] = useState<VairFlow | null>(null);
  const {
    isOpen: isAuthModalOpen,
    openModal: openAuthModal,
    closeModal: closeAuthModal,
  } = useModal();
  const [hasAppliedPrefill, setHasAppliedPrefill] = useState(false);

  // VAIR creator-role gate. The entry buttons only hide the UI; this page is
  // reachable directly (deep link / OAuth round-trip), so we re-check the role
  // here and show an ineligibility modal for connected-but-unauthorized users.
  const { vairUser, vairUserResolved } = useVairPlayerSearch();
  const isVairAmbassador = vairUser?.status.isAmbassador ?? false;
  const isVairPro = getIsVairPro(vairUser);
  const {
    isOpen: isVairIneligibleModalOpen,
    openModal: openVairIneligibleModal,
    closeModal: closeVairIneligibleModal,
  } = useModal();
  const [vairFlowChecked, setVairFlowChecked] = useState(false);

  const [insertEventDraftMutation] = useInsertDraftEventMutation();

  const [insertEventGroupMutation] = useInsertEventGroupMutation();

  const [formData, setFormData] = useState<FormData>({
    eventId: '',
    whenAndWhere: {
      title: '',
      selectDate: (() => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return today;
      })(),
      selectStartTime: 480,
      venue: {
        address: '',
        id: '',
        latitude: 0,
        longitude: 0,
        venueImageURL: '',
        city: null,
      },
      minCourts: 0,
      isVairEvent: false,
      isVairRequired: false,
      scoreLimit: DEFAULT_VAIR_SCORE_LIMIT,
    },
    events: {
      maxEvents: 1,
      events: [
        {
          doubles: '',
          singles: '',
          activeTab: 0,
          eventId: 1,
        },
      ],
    },
    participantsAndGames: [
      {
        eventId: 1,
        maxTeams: 1,
        minGames: 1,
        eventTitle: '',
        eventTab: '',
      },
    ],
  });

  // Apply SEO-tool pre-fill from query params (?players, ?format, ?games, ?minutes).
  // Format values: 'singles' | 'doubles_fixed' | 'doubles_rotating'.
  useEffect(() => {
    if (!router.isReady || hasAppliedPrefill) return;
    const { players, format, games } = router.query;
    const hasAny = players || format || games;
    if (!hasAny) return;

    const playersNum =
      typeof players === 'string' ? parseInt(players, 10) : Number.NaN;
    const gamesNum = typeof games === 'string' ? parseInt(games, 10) : Number.NaN;
    const isSingles = format === 'singles';

    setFormData((prev) => ({
      ...prev,
      events: {
        ...prev.events,
        events: prev.events.events.map((e, i) =>
          i === 0 ? { ...e, activeTab: isSingles ? 1 : 0 } : e,
        ),
      },
      participantsAndGames: prev.participantsAndGames.map((p, i) =>
        i === 0
          ? {
              ...p,
              maxTeams: Number.isFinite(playersNum)
                ? isSingles
                  ? playersNum
                  : Math.max(2, Math.floor(playersNum / 2))
                : p.maxTeams,
              minGames: Number.isFinite(gamesNum) ? gamesNum : p.minGames,
            }
          : p,
      ),
    }));

    setCurrentStep(QuickCreateSteps.WhenAndWhere);
    setHasAppliedPrefill(true);
  }, [router.isReady, router.query, hasAppliedPrefill]);

  // VAIR entry points (?vairFlow=integrated-event | rating-session) skip the
  // generic "Is this a VAIR event?" step and land on SelectVairFormat directly,
  // since the user's intent is already known from the button they clicked.
  // We wait for `vairUserResolved` so the creator role is known, then either
  // proceed (eligible) or open the ineligibility modal (unauthorized).
  useEffect(() => {
    if (!router.isReady || !vairUserResolved || vairFlowChecked) return;
    const flow = router.query.vairFlow;
    if (flow !== 'integrated-event' && flow !== 'rating-session') return;

    // rating-session → VAIR Pros only; integrated-event → ambassadors or Pros
    // (Pros see both, mirroring VairProSection).
    const isEligible = flow === 'rating-session' ? isVairPro : isVairAmbassador || isVairPro;

    if (isEligible) {
      setVairFlow(flow);
      setCurrentStep(QuickCreateSteps.SelectVairFormat);
    } else {
      openVairIneligibleModal();
    }
    setVairFlowChecked(true);
  }, [
    router.isReady,
    router.query.vairFlow,
    vairUserResolved,
    vairFlowChecked,
    isVairAmbassador,
    isVairPro,
    openVairIneligibleModal,
  ]);

  // For VAIR events: only WhenAndWhere counts (totalSteps = 1)
  // For regular events: WhenAndWhere, Events, ParticipantsAndGames (totalSteps = 3)
  const isVairEvent = formData.whenAndWhere?.isVairEvent;
  const totalSteps = isVairEvent ? 1 : 3;

  // This page is statically optimized, so router.query is empty until
  // router.isReady — until then we can't tell a normal load from a VAIR entry,
  // so we show a loader rather than flashing the default WhenAndWhere step on a
  // direct landing (paste / OAuth return). Once ready, we keep the loader for a
  // VAIR entry until the role check has run (→ format cards or ineligible modal).
  const hasVairFlowParam =
    router.query.vairFlow === 'integrated-event' || router.query.vairFlow === 'rating-session';
  const isResolvingVairEntry = hasVairFlowParam && !vairFlowChecked;
  const showLoader = !router.isReady || isResolvingVairEntry;

  const nextStep = () => {
    // From the VAIR format step, advance to WhenAndWhere (payments step is skipped).
    if (currentStep === QuickCreateSteps.SelectVairFormat) {
      setCurrentStep(QuickCreateSteps.WhenAndWhere);
    }
    // For VAIR events, WhenAndWhere calls handleSubmitData directly, then we advance to Success
    else if (currentStep === QuickCreateSteps.WhenAndWhere && formData.whenAndWhere?.isVairEvent) {
      // After handleSubmitData completes, advance to Success
      setCurrentStep(QuickCreateSteps.Success);
    } else {
      const next = currentStep + 1;
      setCurrentStep(next);
    }
  };

  const prevStep = () => {
    // Used by Events / ParticipantsAndGames to walk back one step. WhenAndWhere
    // supplies its own back handler (router.back, or SelectVairFormat for VAIR).
    setCurrentStep(currentStep - 1);
  };

  const handleSubmitData = async (formDataUpdated: FormData) => {
    // Gate the create commit behind auth. Logged-out users can browse and fill
    // out the wizard but cannot submit — they see an auth modal instead.
    if (!userId) {
      setFormData(formDataUpdated);
      openAuthModal();
      return;
    }
    // Handle the final form submission here
    setFormData(formDataUpdated);
    // Submit the data to the server or perform other actions

    const nextDayToStartDate = addDays(formDataUpdated?.whenAndWhere?.selectDate, 1);
    const venueTimezone = formDataUpdated?.whenAndWhere?.venue?.timezone || userTimeZone;

    const dataForUpload = {
      title: formDataUpdated?.whenAndWhere?.title || '',
      description: '',
      registrationPriceUnitAmount: 0,
      registrationDeadlineDate: format(nextDayToStartDate, 'yyyy-MM-dd'),
      registrationDeadlineDateTime: convertToUtc({
        time: getTimeForMinutes(formDataUpdated?.whenAndWhere?.selectStartTime),
        date: nextDayToStartDate,
        timezone: venueTimezone,
      }),
      startDate: format(formDataUpdated?.whenAndWhere?.selectDate, 'yyyy-MM-dd'),
      endDate: format(nextDayToStartDate, 'yyyy-MM-dd'),
      startDateTime: convertToUtc({
        time: getTimeForMinutes(formDataUpdated?.whenAndWhere?.selectStartTime),
        date: formDataUpdated?.whenAndWhere?.selectDate,
        timezone: venueTimezone,
      }),
      endDateTime: convertToUtc({
        time: getTimeForMinutes(formDataUpdated?.whenAndWhere?.selectStartTime),
        date: nextDayToStartDate,
        timezone: venueTimezone,
      }),
      privacy: EventPrivacyEnum.Private,
      isSanctioned: false,
      hasPrizes: false,
      prizeDescription: '',
      venueId: formDataUpdated?.whenAndWhere?.venue?.id || null,
      displayLocation: buildDisplayLocation(
        formDataUpdated?.whenAndWhere?.venue?.city,
        formDataUpdated?.whenAndWhere?.venue?.address,
      ),
      cityId: formDataUpdated?.whenAndWhere?.venue?.city?.id,
      addressString: formDataUpdated?.whenAndWhere?.venue?.address,
      latitude: formDataUpdated?.whenAndWhere?.venue?.latitude,
      longitude: formDataUpdated?.whenAndWhere?.venue?.longitude,
      geometry:
        formDataUpdated?.whenAndWhere?.venue?.latitude &&
        formDataUpdated?.whenAndWhere?.venue?.longitude
          ? {
              type: 'Point',
              coordinates: [
                formDataUpdated?.whenAndWhere?.venue?.longitude,
                formDataUpdated?.whenAndWhere?.venue?.latitude,
              ],
            }
          : null,
      timezoneName: venueTimezone,
      timezoneAbbreviation: getTimezoneAbbreviation(venueTimezone),
      timezoneOffsetMinutes: getTimezoneOffsetMinutes(venueTimezone),
      ballType: BallTypesEnum.NotSelected,
      sport: SportsEnum.Pickleball,
      // is_rating_required predates VAIR and drives the DUPR prompt at registration —
      // quick-create never requires DUPR, so it stays false. The "VAIR required"
      // toggle belongs on is_vair_required.
      isRatingRequired: false,
      isVairRequired: formDataUpdated?.whenAndWhere?.isVairRequired ?? false,
      numberOfCourts: formDataUpdated?.whenAndWhere?.minCourts || 0,
      communityGroupId: communityGroupId,
    };

    let eventId = '';

    /**
     * @todo Is this ever set to published or does it stay draft?
     */
    const insertedEvent = await insertEventDraftMutation({
      variables: {
        object: {
          ...dataForUpload,
          slug: `${slugify(dataForUpload.title.trim(), {
            lower: true,
            trim: true,
            strict: true,
          })}-${generateShortId()}`,
          faqs: {
            data: [],
          },
          sponsors: {
            data: [],
          },
          hostUserId: userId,
          status: EventStatusesEnum.Published,
          publishedAt: 'now()',
          type: EventTypesEnum.Tournament,
          isQuickCreate: true,
        },
      },
    });

    eventId = insertedEvent.data?.insertEventsOne?.id;

    /**
     * @todo this can be done in a single query while inserting the event. If we nest it under the event object, it will take the foreign key of the event.
     */
    const isVairEvent = formDataUpdated?.whenAndWhere?.isVairEvent;

    if (isVairEvent) {
      // VAIR Event: Create event group with VAIR format
      // Teams will be created dynamically during prepare-gameday based on algorithm
      // Player count is unknown at Quick Create (determined at registration)
      // Use max config (9p) for teamLimit; use minimum games (7) since actual games vary by player count
      const vairConfig = getVairConfigForPlayerCount(9);
      await insertEventGroupMutation({
        variables: {
          object: {
            eventId: eventId,
            gender: CompetitionGenderEnum.Mixed,
            format: EventGroupFormatsEnum.VairRoundRobin,
            formatCustomName: '',
            scoringFormat: ScoringFormatEnum.Traditional,
            teamLimit: vairConfig.teamLimit,
            teamType: TeamTypesEnum.Doubles, // VAIR is always doubles
            gamesPerMatch: 1,
            minimumNumberOfGames: getMinimumVairGames(),
            numberOfEstimatedCourts: getMaximumVairCourts(),
            priceUnitAmount: 0,
            title: formDataUpdated?.whenAndWhere?.title || 'VAIR Event',
            totalPoints: formDataUpdated?.whenAndWhere?.scoreLimit ?? DEFAULT_VAIR_SCORE_LIMIT,
            startsAt: convertToUtc({
              time: getTimeForMinutes(formDataUpdated?.whenAndWhere?.selectStartTime),
              date: formDataUpdated?.whenAndWhere?.selectDate,
              timezone: venueTimezone,
            }),
            endsAt: convertToUtc({
              time: getTimeForMinutes(formDataUpdated?.whenAndWhere?.selectStartTime),
              date: nextDayToStartDate,
              timezone: venueTimezone,
            }),
            winBy: 1,
            // VAIR-native rating session (VAIR Round Robin) — results submit to VAIR.
            isVairRatingSession: true,
          },
        },
      });
    } else {
      // Regular Event: Use existing logic
      for await (const event of formDataUpdated?.events?.events) {
        const loadEventParticipantData = formDataUpdated?.participantsAndGames?.find(
          (participant) => {
            return participant?.eventId === event.eventId;
          },
        );

        await insertEventGroupMutation({
          variables: {
            object: {
              eventId: eventId,
              gender: CompetitionGenderEnum.Mixed,
              format: EventGroupFormatsEnum.RoundRobinSingleElimination,
              formatCustomName: '',
              scoringFormat: ScoringFormatEnum.Traditional,
              teamLimit: loadEventParticipantData?.maxTeams || 1,
              teamType: event?.activeTab === 0 ? TeamTypesEnum.Doubles : TeamTypesEnum.Singles,
              gamesPerMatch: loadEventParticipantData?.minGames || 1,
              minimumNumberOfGames: loadEventParticipantData?.minGames || 1,
              priceUnitAmount: 0,
              title: buildTournamentEventNameWithoutGender({
                eventType: eventTypeEnumToString(
                  event?.activeTab === 0 ? TeamTypesEnum.Doubles : TeamTypesEnum.Singles,
                ),
                title: event?.activeTab === 0 ? event?.doubles : event?.singles,
              }),
              totalPoints: 15,
              startsAt: convertToUtc({
                time: getTimeForMinutes(formDataUpdated?.whenAndWhere?.selectStartTime),
                date: formDataUpdated?.whenAndWhere?.selectDate,
                timezone: venueTimezone,
              }),
              endsAt: convertToUtc({
                time: getTimeForMinutes(formDataUpdated?.whenAndWhere?.selectStartTime),
                date: nextDayToStartDate,
                timezone: venueTimezone,
              }),
              winBy: 1,
            },
          },
        });
      }
    }

    if (eventId) {
      setFormData({ ...formData, eventId });
      nextStep(); // This should navigate to the next step/component
    }
  };

  const getCurrentStep = () => {
    if (isVairEvent) {
      if (currentStep === QuickCreateSteps.WhenAndWhere) {
        return 1;
      } else if (currentStep === QuickCreateSteps.Success) {
        return 2;
      } else {
        return 0;
      }
    } else {
      return currentStep;
    }
  };

  return (
    <>
      <Head
        title="Create a Round Robin for Pickleball"
        description="Automatically create round robins for pickleball with a schedule of play, score keeping, and court management. Generate single elimination using the round robin pool play results."
        canonicalType="currentUrlStripQuery"
      />
      <header className="sticky top-0 z-30 flex justify-between border-color-border-input-lightmode bg-white pb-4 pl-8 pr-8 pt-4 md:border-b">
        <h1 className="typography-product-heading-compact flex w-full justify-center pl-4 sm:hidden">
          Create tournament
        </h1>
        <button className="hidden items-center sm:flex" onClick={() => router.back()}>
          <ChevronLeft className="mr-4 h-[1.5rem] w-[1.5rem]" />
          <h1 className="typography-product-heading-compact ">Create tournament</h1>
        </button>
        <button className="flex items-center" onClick={() => router.back()}>
          <CloseIcon className="h-[1.5rem] w-[1.5rem]" />
        </button>
      </header>
      <main className="flex flex-1 flex-col  gap-4 ">
        <div className="flex flex-1 flex-col items-center">
          {showLoader ? (
            <div className="flex flex-1 items-center justify-center py-20">
              <LoadSpinner className="h-8 w-8 animate-spin" />
            </div>
          ) : (
            <>
              {currentStep === QuickCreateSteps.SelectVairFormat ? null : (
                <CircularProgressBar currentStep={getCurrentStep()} totalSteps={totalSteps} />
              )}
              {currentStep === QuickCreateSteps.SelectVairFormat && vairFlow && (
                <SelectVairFormat
                  vairFlow={vairFlow}
                  nextStep={nextStep}
                  prevStep={() => router.back()}
                  formData={formData}
                  setFormData={setFormData}
                />
              )}
              {currentStep === QuickCreateSteps.WhenAndWhere && (
                <WhenAndWhere
                  nextStep={nextStep}
                  // Back returns to the format step for VAIR entries, otherwise leaves the page.
                  prevStep={
                    vairFlow
                      ? () => setCurrentStep(QuickCreateSteps.SelectVairFormat)
                      : () => router.back()
                  }
                  formData={formData}
                  setFormData={setFormData}
                  handleSubmitData={handleSubmitData}
                />
              )}
              {currentStep === QuickCreateSteps.Events && (
                <Events
                  nextStep={nextStep}
                  prevStep={prevStep}
                  formData={formData}
                  setFormData={setFormData}
                />
              )}
              {currentStep === QuickCreateSteps.ParticipantsAndGames && (
                <ParticipantsAndGames
                  nextStep={nextStep}
                  prevStep={prevStep}
                  formData={formData}
                  handleSubmitData={handleSubmitData}
                />
              )}
              {currentStep === QuickCreateSteps.Success && <Success formData={formData} />}
            </>
          )}
        </div>
      </main>
      <AuthRequiredModal isOpen={isAuthModalOpen} onClose={closeAuthModal} />
      <VairIneligibleModal
        isOpen={isVairIneligibleModalOpen}
        onClose={closeVairIneligibleModal}
      />
    </>
  );
}

function AuthRequiredModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const router = useRouter();
  const nextHref = router.asPath || TOURNAMENT_QUICK_CREATE_PAGE;
  const nextParam = encodeURIComponent(nextHref);
  return (
    <Modal isOpen={isOpen} handleClose={onClose} classNameMaxWidth="sm:max-w-md">
      <div className="px-6 py-8 text-center sm:px-8">
        <h2 className="typography-product-heading mb-2 text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
          Sign up to create your tournament
        </h2>
        <p className="typography-product-body mb-6 text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
          A free Bounce account lets you save the schedule, share it with players, score games
          live, and post results.
        </p>
        <div className="flex flex-col items-stretch gap-3">
          <ButtonLink
            href={`${SIGNUP_PAGE}?next=${nextParam}`}
            variant="primary"
            size="lg"
            isInline
          >
            Sign up free
          </ButtonLink>
          <ButtonLink
            href={`${LOGIN_PAGE}?next=${nextParam}`}
            variant="secondary"
            size="lg"
            isInline
          >
            Log in
          </ButtonLink>
          <button
            type="button"
            onClick={onClose}
            className="mt-1 text-sm text-color-text-lightmode-tertiary hover:underline dark:text-color-text-darkmode-tertiary"
          >
            Keep browsing
          </button>
        </div>
      </div>
    </Modal>
  );
}

// Shown when a user reaches a VAIR creation flow (?vairFlow=) without the
// matching VAIR role. The entry buttons hide the UI, but the page is reachable
// directly, so this is the defensive fallback for unauthorized access.
function VairIneligibleModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  return (
    <Modal isOpen={isOpen} handleClose={onClose} classNameMaxWidth="sm:max-w-md">
      <div className="px-6 py-8 text-center sm:px-8">
        <h2 className="typography-product-heading mb-2 text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
          VAIR event creation isn’t available
        </h2>
        <p className="typography-product-body mb-6 text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
          Creating VAIR events is reserved for VAIR Ambassadors and Raters. If you think this is a
          mistake, check your VAIR account status.
        </p>
        <div className="flex flex-col items-stretch gap-3">
          <ButtonLink href={HOME_PAGE} variant="primary" size="lg" isInline>
            Back to home
          </ButtonLink>
        </div>
      </div>
    </Modal>
  );
}
